import { ref, computed, onBeforeMount, onUnmounted, onMounted, nextTick } from "vue"
import { useStore } from "vuex"
import SearchInputField from "../SearchInputField/SearchInputField.vue"
import ChatMessage from "../ChatPanel/ChatMessage/ChatMessage";
import debounce from "../../foundation/js/debounce"
import { useViewport } from "../../composables/use-viewport.composable";
import { FEEDBACK_TYPES } from "../FeedbackToolbar/feedback.constants";

export default {
  name: "AiAssistant",
  components: {
    SearchInputField,
    ChatMessage
  },
  props: {
    searchUrl: {
      type: String,
      required: true,
    },
    lang: {
      type: String,
      required: false,
      default: null
    },
    labels: {
      type: Object,
      required: true,
    },
    ariaLabels: {
      type: Object,
      required: true,
    },
    configs: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: false,
      default: null
    },
    hint: {
      type: String,
      required: false,
      default: null
    },
  },
  setup(props) {
    const pageContainer = ref(undefined);
    const pageHeroContainer = ref(undefined);
    const pageHeadlineContainer = ref(undefined);
    const store = useStore()
    const actionTaken = ref(false)
    const messagesContainer = ref(undefined)
    const query = ref("")
    const expanded = ref(false)
    const { viewport } = useViewport();

    let isFirstMessage = true;
    let viewportContainer;
    let pagePaddingTop;
    let queryParamActionId;

    const messages = computed(() => {
      return store.state.aiAssistant.messages
    });

    const awaitingResponse = computed(() => {
      return store.state.aiAssistant.awaitingResponse
    })

    onBeforeMount(() => {
      document.documentElement.classList.add('--body-scrollable-only');
      viewportContainer = document.querySelector('#main-container');

      const urlParams = new URLSearchParams(window.location.search);
      const extractedQuery = urlParams.get("q");
      queryParamActionId = extractedQuery ? null : urlParams.get("action-id");

      expanded.value = !!(extractedQuery || queryParamActionId);
      query.value = extractedQuery || '';

      if (!queryParamActionId) {
        search(extractedQuery);
      }
    })

    onMounted(async () => {
      window.addEventListener('resize', adjustElements)

      await nextTick();
      adjustElements();

      if (queryParamActionId) {
        await store.dispatch('aiAssistant/handleActionIdFromQueryParam', {
          ariaLabels: props.ariaLabels,
          filters: props.filters,
        });
        isFirstMessage = false;
        scrollWithDebounce();
      }
    });

    onUnmounted(() => {
      window.removeEventListener('resize', adjustElements)
    })

    const adjustElements = () => {
      updateHeadlineWidth();
      updatePagePaddingTop();
    }

    const updateHeadlineWidth = () => {
      pageHeadlineContainer.value.style.width = `${pageHeroContainer.value.offsetWidth}px`;
    }

    const updatePagePaddingTop = () => {
      pagePaddingTop = parseInt(window.getComputedStyle(pageContainer.value).paddingTop, 10);
    }

    const updateSearchKeyword = (value) => {
      query.value = value
    }

    const resetWindowScroll = () => {
      // mobile fix scroll to 0 at the window level
      window.scrollTo({ top: 0 });
    };

    const search = (keyword) => {
      resetWindowScroll();
      expanded.value = !!keyword;
      query.value = keyword ? keyword : query.value
      store.dispatch('aiAssistant/start', {
        message: query.value,
        introActionId: props.configs.introActionId,
        enableSearch: props.configs.enableSearch,
        searchKeywordBlacklist: props.configs.searchKeywordBlacklist,
        ariaLabels: props.ariaLabels,
        isNewSearch: !expanded.value,
        lang: props.lang,
        apiUrl: props.searchUrl,
        page: 1,
        filters: props.filters,
      });

      scrollWithDebounce();

      if (!query.value) {
        store.dispatch("aiAssistant/clearMessages")
      }

      updateUrl(query.value)
    }

    const handleAction = (actionId, isFloatingTrigger = false) => {
      resetWindowScroll();
      actionTaken.value = true
      scrollWithDebounce();
      store.dispatch("aiAssistant/sendActionId", { actionId, isFloatingTrigger });
    }

    const handleExternalLink = (payload) => {
      store.dispatch("aiAssistant/sendExternalLink", payload);
    }

    const updateUrl = (value) => {
      const url = new URL(window.location)
      url.searchParams.set("q", value)
      window.history.pushState({}, "", url)
    }

    const handleFeedback = (feedbackPayload) => {
      actionTaken.value = true;
      if (feedbackPayload.feedback === FEEDBACK_TYPES.NEGATIVE && props.configs.negativeFeedbackActionId) {
        handleAction(props.configs.negativeFeedbackActionId, true)
      }
    }

    const scrollWithDebounce = debounce(() => {
      if (messagesContainer.value) {
        const lastMessageGroups = messagesContainer.value.querySelectorAll('.nyk-ai-assistant__messages__scroll-anchor');
        if (!lastMessageGroups || lastMessageGroups.length === 0) return;
        const lastMessage = lastMessageGroups[lastMessageGroups.length - 1];

        if (!isFirstMessage) {
          const diffOffset = (lastMessage.offsetTop - viewportContainer.offsetTop);
          messagesContainer.value.style.minHeight = `${viewportContainer.clientHeight + diffOffset}px`;
        }

        if (isFirstMessage) {
          isFirstMessage = false;
        }

        viewportContainer.scrollTo({
          behavior: 'smooth',
          top: lastMessage.offsetTop - pagePaddingTop
        });

        if (actionTaken.value) {
          actionTaken.value = false
        }
      }
    }, 50)


    return {
      query,
      pageContainer,
      pageHeroContainer,
      pageHeadlineContainer,
      viewport,
      actionTaken,
      messagesContainer,
      expanded,
      messages,
      search,
      handleAction,
      handleExternalLink,
      updateSearchKeyword,
      handleFeedback,
      awaitingResponse
    }
  },
}
